// =============================================
// Akountant Search Engine — Self-contained, fast, in-memory
// Inverted index with prefix matching & scoring
// =============================================

export interface SearchItem {
  id: string;
  service: string;         // e.g. 'Contacts', 'Accounting', 'Calendar'
  serviceKey: string;      // e.g. 'contacts', 'accounting', 'calendar'
  type: string;            // e.g. 'contact', 'invoice', 'event', 'page'
  title: string;           // Primary display text
  subtitle?: string;       // Secondary text (email, description, etc.)
  icon?: string;           // Icon identifier
  path: string;            // Navigation path
  keywords: string[];      // Extra searchable terms
  timestamp?: number;      // For recency scoring
}

interface IndexEntry {
  itemId: string;
  field: string;           // Which field matched
  score: number;           // Relevance weight
}

// Inverted index: token → list of matching items
type InvertedIndex = Map<string, IndexEntry[]>;

class SearchEngine {
  private items: Map<string, SearchItem> = new Map();
  private index: InvertedIndex = new Map();
  private allowedServices: Set<string> = new Set();

  // ---- Indexing ----

  /** Clear everything and rebuild */
  clear(): void {
    this.items.clear();
    this.index.clear();
  }

  /** Set which services the current user can access */
  setAllowedServices(services: string[]): void {
    this.allowedServices = new Set(services.map(s => s.toLowerCase()));
  }

  /** Add a single item to the index */
  addItem(item: SearchItem): void {
    this.items.set(item.id, item);

    // Index title tokens (highest weight)
    this.tokenize(item.title).forEach(token => {
      this.addToIndex(token, { itemId: item.id, field: 'title', score: 10 });
    });

    // Index subtitle (medium weight)
    if (item.subtitle) {
      this.tokenize(item.subtitle).forEach(token => {
        this.addToIndex(token, { itemId: item.id, field: 'subtitle', score: 5 });
      });
    }

    // Index service name (medium weight)
    this.tokenize(item.service).forEach(token => {
      this.addToIndex(token, { itemId: item.id, field: 'service', score: 4 });
    });

    // Index type (low weight)
    this.tokenize(item.type).forEach(token => {
      this.addToIndex(token, { itemId: item.id, field: 'type', score: 3 });
    });

    // Index keywords (medium weight)
    item.keywords.forEach(kw => {
      this.tokenize(kw).forEach(token => {
        this.addToIndex(token, { itemId: item.id, field: 'keyword', score: 6 });
      });
    });
  }

  /** Add many items at once */
  addItems(items: SearchItem[]): void {
    items.forEach(item => this.addItem(item));
  }

  // ---- Searching ----

  /** Search across all indexed items, filtered by user privileges */
  search(query: string, options: {
    limit?: number;
    serviceFilter?: string;   // Filter to a specific service
  } = {}): SearchResult[] {
    const { limit = 20, serviceFilter } = options;

    if (!query.trim()) return [];

    const tokens = this.tokenize(query);
    if (tokens.length === 0) return [];

    // Collect scores per item
    const scores = new Map<string, number>();
    const matchedFields = new Map<string, Set<string>>();

    for (const token of tokens) {
      const entries = this.prefixSearch(token);
      for (const entry of entries) {
        const item = this.items.get(entry.itemId);
        if (!item) continue;

        // Filter by allowed services
        if (this.allowedServices.size > 0 && !this.allowedServices.has(item.serviceKey.toLowerCase())) {
          continue;
        }

        // Filter by specific service if requested
        if (serviceFilter && item.serviceKey.toLowerCase() !== serviceFilter.toLowerCase()) {
          continue;
        }

        // Accumulate score
        const current = scores.get(entry.itemId) || 0;
        let bonus = 0;

        // Exact match bonus
        if (this.tokenize(item.title).includes(token)) bonus += 5;

        // Recency bonus (items from last 7 days)
        if (item.timestamp && Date.now() - item.timestamp < 7 * 24 * 60 * 60 * 1000) {
          bonus += 2;
        }

        scores.set(entry.itemId, current + entry.score + bonus);

        // Track which fields matched
        if (!matchedFields.has(entry.itemId)) matchedFields.set(entry.itemId, new Set());
        matchedFields.get(entry.itemId)!.add(entry.field);
      }
    }

    // Build results, sorted by score
    const results: SearchResult[] = [];
    scores.forEach((score, itemId) => {
      const item = this.items.get(itemId);
      if (!item) return;

      results.push({
        item,
        score,
        matchedFields: Array.from(matchedFields.get(itemId) || []),
      });
    });

    results.sort((a, b) => b.score - a.score);
    return results.slice(0, limit);
  }

  /** Get results grouped by service */
  searchGrouped(query: string, limit: number = 20): Map<string, SearchResult[]> {
    const results = this.search(query, { limit: limit * 2 });
    const grouped = new Map<string, SearchResult[]>();

    for (const result of results) {
      const svc = result.item.service;
      if (!grouped.has(svc)) grouped.set(svc, []);
      const group = grouped.get(svc)!;
      if (group.length < 5) group.push(result); // Max 5 per service
    }

    return grouped;
  }

  /** Get total indexed item count */
  get size(): number {
    return this.items.size;
  }

  // ---- Internal helpers ----

  private tokenize(text: string): string[] {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(t => t.length >= 2);
  }

  private addToIndex(token: string, entry: IndexEntry): void {
    if (!this.index.has(token)) {
      this.index.set(token, []);
    }
    this.index.get(token)!.push(entry);
  }

  /** Prefix search: "inv" matches "invoice", "inventory", etc. */
  private prefixSearch(prefix: string): IndexEntry[] {
    const results: IndexEntry[] = [];

    // Exact match first
    const exact = this.index.get(prefix);
    if (exact) results.push(...exact);

    // Prefix matches (scan index keys)
    // For large datasets this should use a trie; Map scan is fine for <100k items
    if (prefix.length >= 2) {
      this.index.forEach((entries: IndexEntry[], token: string) => {
        if (token !== prefix && token.startsWith(prefix)) {
          // Slightly lower score for prefix matches
          results.push(...entries.map((e: IndexEntry) => ({ ...e, score: e.score * 0.8 })));
        }
      });
    }

    return results;
  }
}

export interface SearchResult {
  item: SearchItem;
  score: number;
  matchedFields: string[];
}

// =============================================
// Singleton instance
// =============================================
export const searchEngine = new SearchEngine();

// =============================================
// Seed demo data for development
// =============================================
export function seedSearchData(): void {
  searchEngine.clear();

  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;

  const demoItems: SearchItem[] = [
    // -- Contacts --
    { id: 'c1', service: 'Contacts', serviceKey: 'contacts', type: 'contact', title: 'John Smith', subtitle: 'john@acme.com', path: '/dashboard/contacts/1', keywords: ['client', 'acme', 'corporation'], timestamp: now - 1 * day },
    { id: 'c2', service: 'Contacts', serviceKey: 'contacts', type: 'contact', title: 'Sarah Johnson', subtitle: 'sarah@widgets.com', path: '/dashboard/contacts/2', keywords: ['vendor', 'widget', 'solutions'], timestamp: now - 2 * day },
    { id: 'c3', service: 'Contacts', serviceKey: 'contacts', type: 'contact', title: 'Mike Chen', subtitle: 'mike@techglobal.io', path: '/dashboard/contacts/3', keywords: ['partner', 'tech', 'global'], timestamp: now - 3 * day },
    { id: 'c4', service: 'Contacts', serviceKey: 'contacts', type: 'contact', title: 'Emily Davis', subtitle: 'emily@startup.co', path: '/dashboard/contacts/4', keywords: ['investor', 'startup'], timestamp: now - 5 * day },
    { id: 'c5', service: 'Contacts', serviceKey: 'contacts', type: 'contact', title: 'Robert Wilson', subtitle: 'robert@legal.com', path: '/dashboard/contacts/5', keywords: ['lawyer', 'legal', 'attorney'], timestamp: now },

    // -- Accounting --
    { id: 'a1', service: 'Accounting', serviceKey: 'accounting', type: 'invoice', title: 'Invoice #1042', subtitle: 'Acme Corp - $4,200.00', path: '/dashboard/bookkeeping/inv/1042', keywords: ['unpaid', 'outstanding', 'net30'], timestamp: now - 1 * day },
    { id: 'a2', service: 'Accounting', serviceKey: 'accounting', type: 'invoice', title: 'Invoice #1043', subtitle: 'Widget Solutions - $890.00', path: '/dashboard/bookkeeping/inv/1043', keywords: ['paid', 'completed'], timestamp: now - 3 * day },
    { id: 'a3', service: 'Accounting', serviceKey: 'accounting', type: 'expense', title: 'Office Supplies', subtitle: '$245.50 - Staples', path: '/dashboard/bookkeeping/exp/101', keywords: ['expense', 'supplies', 'staples', 'receipt'], timestamp: now - 2 * day },
    { id: 'a4', service: 'Accounting', serviceKey: 'accounting', type: 'expense', title: 'Software Subscription', subtitle: '$99.00/mo - Adobe Creative', path: '/dashboard/bookkeeping/exp/102', keywords: ['recurring', 'subscription', 'adobe', 'software'], timestamp: now },
    { id: 'a5', service: 'Accounting', serviceKey: 'accounting', type: 'receipt', title: 'Receipt #R-2089', subtitle: 'Client payment - $1,500.00', path: '/dashboard/bookkeeping/rcpt/2089', keywords: ['payment', 'received', 'deposit'], timestamp: now - 1 * day },
    { id: 'a6', service: 'Accounting', serviceKey: 'accounting', type: 'report', title: 'Q2 Financial Report', subtitle: 'April - June 2026', path: '/dashboard/bookkeeping/reports/q2', keywords: ['quarterly', 'financial', 'profit', 'loss', 'balance'], timestamp: now - 5 * day },

    // -- Documents --
    { id: 'd1', service: 'Documents', serviceKey: 'dms', type: 'document', title: 'Tax Return 2025', subtitle: 'PDF - 2.4 MB', path: '/dashboard/dms/doc/1', keywords: ['tax', 'return', 'filing', '2025', 'pdf'], timestamp: now - 10 * day },
    { id: 'd2', service: 'Documents', serviceKey: 'dms', type: 'document', title: 'Partnership Agreement', subtitle: 'DOCX - 156 KB', path: '/dashboard/dms/doc/2', keywords: ['contract', 'agreement', 'legal', 'partnership'], timestamp: now - 30 * day },
    { id: 'd3', service: 'Documents', serviceKey: 'dms', type: 'document', title: 'Employee Handbook', subtitle: 'PDF - 1.8 MB', path: '/dashboard/dms/doc/3', keywords: ['hr', 'handbook', 'policy', 'employee'], timestamp: now - 60 * day },
    { id: 'd4', service: 'Documents', serviceKey: 'dms', type: 'folder', title: 'Client Contracts', subtitle: '14 files', path: '/dashboard/dms/folder/1', keywords: ['folder', 'contracts', 'clients'], timestamp: now - 5 * day },
    { id: 'd5', service: 'Documents', serviceKey: 'dms', type: 'document', title: 'Monthly Budget Spreadsheet', subtitle: 'XLSX - 340 KB', path: '/dashboard/dms/doc/5', keywords: ['budget', 'spreadsheet', 'excel', 'monthly'], timestamp: now - 2 * day },

    // -- Calendar --
    { id: 'ev1', service: 'Calendar', serviceKey: 'calendar', type: 'event', title: 'Tax Filing Deadline', subtitle: 'June 15, 2026 - All day', path: '/dashboard/calendar/ev/1', keywords: ['deadline', 'tax', 'filing', 'june'], timestamp: now + 10 * day },
    { id: 'ev2', service: 'Calendar', serviceKey: 'calendar', type: 'event', title: 'Client Meeting - Acme Corp', subtitle: 'June 8, 2026 - 2:00 PM', path: '/dashboard/calendar/ev/2', keywords: ['meeting', 'acme', 'client', 'review'], timestamp: now + 3 * day },
    { id: 'ev3', service: 'Calendar', serviceKey: 'calendar', type: 'event', title: 'Quarterly Review', subtitle: 'June 30, 2026 - 10:00 AM', path: '/dashboard/calendar/ev/3', keywords: ['review', 'quarterly', 'q2', 'team'], timestamp: now + 25 * day },
    { id: 'ev4', service: 'Calendar', serviceKey: 'calendar', type: 'reminder', title: 'Renew Business License', subtitle: 'Due: July 1, 2026', path: '/dashboard/calendar/ev/4', keywords: ['license', 'renewal', 'government', 'deadline'], timestamp: now + 26 * day },

    // -- Tax Filing --
    { id: 't1', service: 'Tax Filing', serviceKey: 'tax-filing', type: 'filing', title: 'Federal Tax Return 2025', subtitle: 'Status: Filed', path: '/dashboard/tax-filing/1', keywords: ['federal', 'irs', 'filed', '1040', 'income'], timestamp: now - 30 * day },
    { id: 't2', service: 'Tax Filing', serviceKey: 'tax-filing', type: 'filing', title: 'State Tax - California', subtitle: 'Status: Pending', path: '/dashboard/tax-filing/2', keywords: ['state', 'california', 'pending', 'franchise'], timestamp: now - 5 * day },
    { id: 't3', service: 'Tax Filing', serviceKey: 'tax-filing', type: 'filing', title: 'GST/HST Return - Q1', subtitle: 'Status: Draft', path: '/dashboard/tax-filing/3', keywords: ['gst', 'hst', 'canada', 'quarterly', 'sales tax'], timestamp: now - 2 * day },

    // -- Tasks --
    { id: 'tk1', service: 'Tasks', serviceKey: 'tasks', type: 'task', title: 'Review Acme invoices', subtitle: 'Due: Tomorrow - High priority', path: '/dashboard/tasks/1', keywords: ['review', 'invoice', 'acme', 'urgent', 'high'], timestamp: now },
    { id: 'tk2', service: 'Tasks', serviceKey: 'tasks', type: 'task', title: 'Prepare monthly payroll', subtitle: 'Due: June 10 - Medium priority', path: '/dashboard/tasks/2', keywords: ['payroll', 'monthly', 'salary', 'employees'], timestamp: now - 1 * day },
    { id: 'tk3', service: 'Tasks', serviceKey: 'tasks', type: 'task', title: 'Update client contact list', subtitle: 'Due: June 12 - Low priority', path: '/dashboard/tasks/3', keywords: ['update', 'contacts', 'list', 'cleanup'], timestamp: now - 3 * day },

    // -- Navigation pages (always accessible) --
    { id: 'nav1', service: 'Navigation', serviceKey: 'navigation', type: 'page', title: 'Dashboard', subtitle: 'Home overview', path: '/dashboard', keywords: ['home', 'overview', 'dashboard', 'main'] },
    { id: 'nav2', service: 'Navigation', serviceKey: 'navigation', type: 'page', title: 'Settings', subtitle: 'Account & preferences', path: '/dashboard/settings', keywords: ['settings', 'preferences', 'account', 'profile'] },
    { id: 'nav3', service: 'Navigation', serviceKey: 'navigation', type: 'page', title: 'Documents', subtitle: 'File management', path: '/dashboard/dms', keywords: ['documents', 'files', 'dms', 'upload'] },
    { id: 'nav4', service: 'Navigation', serviceKey: 'navigation', type: 'page', title: 'Bookkeeping', subtitle: 'Financial management', path: '/dashboard/bookkeeping', keywords: ['bookkeeping', 'accounting', 'finance', 'money'] },
    { id: 'nav5', service: 'Navigation', serviceKey: 'navigation', type: 'page', title: 'Tax Filing', subtitle: 'Tax returns & compliance', path: '/dashboard/tax-filing', keywords: ['tax', 'filing', 'returns', 'compliance'] },
    { id: 'nav6', service: 'Navigation', serviceKey: 'navigation', type: 'page', title: 'Contacts', subtitle: 'People & companies', path: '/dashboard/contacts', keywords: ['contacts', 'people', 'companies', 'clients'] },
    { id: 'nav7', service: 'Navigation', serviceKey: 'navigation', type: 'page', title: 'Calendar', subtitle: 'Events & deadlines', path: '/dashboard/calendar', keywords: ['calendar', 'events', 'schedule', 'deadlines'] },
  ];

  searchEngine.addItems(demoItems);

  // Allow all services by default (will be filtered per user at runtime)
  searchEngine.setAllowedServices([
    'contacts', 'accounting', 'dms', 'calendar', 'tax-filing', 'tasks', 'navigation',
  ]);
}
